import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty, partition, join, sortBy, uniq, compact, values } from 'lodash';
import i18n from 'utils/i18n';
import ASQFinishSurveyButton from './ASQFinishSurveyButton';
import ASQCheckbox from './ASQCheckbox';
import ASQImage from './ASQImage';
import ASQTextArea from './ASQTextArea';
import ASQRadio from './ASQRadio';

const ASQResponseTypes = {
  checkbox: ASQCheckbox,
  image: ASQImage,
  radio: ASQRadio,
  textarea: ASQTextArea,
};

const asqInput = (asq, entities, required, optional, keyPrefix = 'ro', onChange, onDelete) => {
  const getAnswer = (r) => r.answer ? entities.answers[r.answer] : null;

  if (keyPrefix === 'ro') {
    return optional.map ((r, rIdx) => {
      const InputType = ASQResponseTypes[r.inputType];
      const disabled = required.some((req) => !getAnswer(req));
      const answer = getAnswer(r);

      if (disabled && answer) {
        onDelete(answer.id,{
          asqResponseTypeId: r.id,
          id: answer.id }
        )
      }

      return <InputType
        key={`${keyPrefix}-${rIdx}-${r.id}`}
        disabled={disabled}
        asq={asq}
        responseType={r}
        answer={answer}
        onChange={onChange}
        onDelete={onDelete}
      />
    });
  } else {
    return required.map ((r, rIdx) => {
      const InputType = ASQResponseTypes[r.inputType];
      return <InputType
        key={`${keyPrefix}-${rIdx}-${r.id}`}
        disabled={false}
        asq={asq}
        responseType={r}
        answer={getAnswer(r)}
        onChange={onChange}
        onDelete={onDelete}
      />
    });
  }
}

const getWarningMessage = (sections) => {
  const qNum = join(sortBy(uniq(compact(sections.map(s => s.requiredAnswersCount)))), ',');
  return i18n.t('asq.survey.scoringWarning', { count: qNum });
}

const ASQSurvey = ({ isValid, survey, onChange, onDelete, onGoBack, onFinishSurvey }) => {
  if (isEmpty(survey)) return null;
  if (survey.entities.surveys[survey.result].status === 'finished') {
    onGoBack();
    return null;
  }
  const entities = survey.entities;
  const asq = entities.surveys[survey.result];
  const sEntities = entities.questionnaires[asq.questionnaire].sections;
  const sLength = sEntities.length - 1;
  const sections = sEntities.map( (sKey, sIdx) => {
    const s = entities.sections[sKey];
    const questions = s.questions.map( (q, qIdx) => {
      const qLength = s.questions.length - 1;
      const question = entities.questions[q];
      const responses = question.responseTypes.map(i => entities.responseTypes[i]);
      const [ required, optional ] = partition(responses, {'inputType': 'radio'});
      return (
        <div key={`s${s.id}-q${q}`} className='o-question row'>
          <div className='column shrink u-padding-right--zero'>
            <span className='o-question__number'>{qIdx + 1}</span>
          </div>
          <div className='column'>
            <div className='row'>
              <div className='c-survey-question__content'>
                <p className='u-margin-bottom--small'><b>{question.boldBody}</b></p>
                { question.body && <p>{question.body}</p> }
                { question.imageUrl &&
                  <div className='o-question__image text-center u-padding-bottom--small'>
                    <img alt='' src={question.imageUrl}></img>
                  </div> }
                {asqInput(asq, entities, required, optional, 'ro', onChange, onDelete)}
              </div>
              <div className='c-survey-question__controls'>
                {asqInput(asq, entities, required, optional, 'rm', onChange, onDelete)}
              </div>
            </div>
            {(qIdx !== qLength) && <hr className='u-hr--answer'/>}
          </div>
        </div>
      );
    });
    return (
      <section id={`section-${sIdx + 1}`} key={`section-${s.id}`} className='o-section'>
        <div className='u-txt--survey-title u-margin-bottom--rem'>{s.name}</div>
        {questions}
        {(sIdx !== sLength) && <hr className='u-hr--section'/>}
      </section>
    )
  })
  return (
    <div id='sp-asq-survey' className='column o-sections o-sections--base u-margin-top--large u-margin-bottom--base cs-bg--light'>
      {sections}
      <ASQFinishSurveyButton isValid={isValid} onFinishSurvey={onFinishSurvey}
        message={isValid ? null : getWarningMessage(values(entities.sections))} />
    </div>
  );
}

ASQSurvey.propTypes = {
  isValid: PropTypes.bool.isRequired,
  survey: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onFinishSurvey: PropTypes.func.isRequired,
  onGoBack: PropTypes.func.isRequired,
}

export default ASQSurvey;
